import { Suspense, lazy, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { api } from "./api";
import { redirectToDiscordLogin } from "./authNavigation";
import { AddonsV2, CommandsV2, MemberDetailV2, MembersV2, PortalAdminV2, RoleStudioV2, SettingsV2 } from "./AdminV2";
import { Audit, Logs, MinecraftAdmin, Overview, Punishments, System } from "./AdminOps";
import { CommunityLeaderboard, CommunityLeaderboards } from "./Community";
import { MinecraftIslandTopV2, MinecraftServerV2, MinecraftShell } from "./MinecraftV2";
import { Portal } from "./Portal";
import { DiscordMark, Icon, type IconName } from "./icons";
import { AppealPage, JoinPage, TeamPage } from "./site/Community";
import { NewsArticle, NewsIndex } from "./site/News";
import { VotePage } from "./site/Vote";
import { SiteProvider, useSite } from "./site/context";
import { StoreDialogs } from "./site/store/StoreDialogs";
import { StoreProvider } from "./site/store/StoreSession";
import { Avatar, EmptyState, FullState, SessionContext, Skeleton, errorMessage, type Me } from "./ui";
import { ConfirmProvider, ToastProvider } from "./ui/admin";
import seoPages from "../../shared/seo-pages.json";

// Staff, ticket, and newer admin pages load on demand so public visitors do not download them.
const ServerAuditPage = lazy(() => import("./ServerAudit").then((module) => ({ default: module.ServerAuditPage })));
const PageAccess = lazy(() => import("./admin/PageAccess").then((module) => ({ default: module.PageAccess })));
const SiteSettings = lazy(() => import("./admin/SiteSettings").then((module) => ({ default: module.SiteSettings })));
const StaffDetail = lazy(() => import("./admin/StaffDirectory").then((module) => ({ default: module.StaffDetail })));
const StaffDirectory = lazy(() => import("./admin/StaffDirectory").then((module) => ({ default: module.StaffDirectory })));
const NewsAdmin = lazy(() => import("./admin/News").then((module) => ({ default: module.NewsAdmin })));
const StoreManager = lazy(() => import("./admin/store/StoreManager").then((module) => ({ default: module.StoreManager })));
const GiftCardsPage = lazy(() => import("./admin/store/GiftCardsPage").then((module) => ({ default: module.GiftCardsPage })));
const OrdersAdminPage = lazy(() => import("./admin/store/OrdersAdminPage").then((module) => ({ default: module.OrdersAdminPage })));
const StoreSettingsPage = lazy(() => import("./admin/store/StoreSettingsPage").then((module) => ({ default: module.StoreSettingsPage })));
const OrbsPage = lazy(() => import("./admin/store/OrbsPage").then((module) => ({ default: module.OrbsPage })));
const StorePage = lazy(() => import("./site/store/StorePage").then((module) => ({ default: module.StorePage })));
const CartPage = lazy(() => import("./site/store/CartPage").then((module) => ({ default: module.CartPage })));
const CheckoutPage = lazy(() => import("./site/store/CheckoutPage").then((module) => ({ default: module.CheckoutPage })));
const WalletPage = lazy(() => import("./site/store/WalletPage").then((module) => ({ default: module.WalletPage })));
const OrdersPage = lazy(() => import("./site/store/OrdersPage").then((module) => ({ default: module.OrdersPage })));
const OrderPageView = lazy(() => import("./site/store/OrdersPage").then((module) => ({ default: module.OrderPageView })));
const TicketInsights = lazy(() => import("./admin/TicketInsights").then((module) => ({ default: module.TicketInsights })));
const AppealRoutes = lazy(() => import("./staff/Appeals").then((module) => ({ default: module.AppealRoutes })));
const ResourceRoutes = lazy(() => import("./staff/Resources").then((module) => ({ default: module.ResourceRoutes })));
const StaffHub = lazy(() => import("./staff/StaffHub").then((module) => ({ default: module.StaffHub })));
const StaffTicketRoutes = lazy(() => import("./tickets/Tickets").then((module) => ({ default: module.StaffTicketRoutes })));
const TicketRedirect = lazy(() => import("./tickets/Tickets").then((module) => ({ default: module.TicketRedirect })));

type NavItem = { label: string; href: string; permission: string; group: string; icon: IconName; aliases?: string[]; feature?: "tickets" };
const adminNav: NavItem[] = [
  { label: "Overview", href: "/discord/admin/overview", permission: "dashboard.view", group: "Workspace", icon: "overview" },
  { label: "Members", href: "/discord/admin/members", permission: "members.view", group: "Community", icon: "members", aliases: ["/members/"] },
  { label: "Punishments", href: "/discord/admin/punishments", permission: "punishments.view", group: "Community", icon: "gavel" },
  { label: "Role Studio", href: "/discord/admin/reaction-roles", permission: "reactionroles.view", group: "Community", icon: "roles" },
  { label: "Staff", href: "/discord/admin/staff", permission: "staff.directory.view", group: "Community", icon: "shield" },
  { label: "Tickets", href: "/discord/admin/tickets", permission: "tickets.insights.view", group: "Community", icon: "ticket", feature: "tickets" },
  { label: "News", href: "/discord/admin/news", permission: "news.manage", group: "Community", icon: "megaphone" },
  { label: "Store", href: "/discord/admin/store", permission: "store.manage", group: "Store", icon: "tag" },
  { label: "Store orders", href: "/discord/admin/orders", permission: "store.orders.view", group: "Store", icon: "inbox" },
  { label: "Store settings", href: "/discord/admin/store-settings", permission: "store.manage", group: "Store", icon: "settings" },
  { label: "Gift cards", href: "/discord/admin/gift-cards", permission: "store.giftcards.manage", group: "Store", icon: "ticket" },
  { label: "Orbs", href: "/discord/admin/orbs", permission: "store.orbs.adjust", group: "Store", icon: "coins" },
  { label: "Member portal", href: "/discord/admin/memberportal", permission: "portal.configure", group: "Community", icon: "portal" },
  { label: "Website", href: "/discord/admin/website", permission: "site.access.configure", group: "Configuration", icon: "globe" },
  { label: "Page access", href: "/discord/admin/page-access", permission: "site.access.configure", group: "Configuration", icon: "eye" },
  { label: "Add-ons", href: "/discord/admin/addons", permission: "addons.view", group: "Configuration", icon: "addons" },
  { label: "Commands", href: "/discord/admin/commands", permission: "commands.view", group: "Configuration", icon: "commands" },
  { label: "Settings", href: "/discord/admin/settings", permission: "config.view", group: "Configuration", icon: "settings" },
  { label: "Minecraft", href: "/discord/admin/minecraft", permission: "minecraft.integration.manage", group: "Operations", icon: "cube" },
  { label: "Logs", href: "/discord/admin/logs", permission: "logs.view", group: "Operations", icon: "logs" },
  { label: "Server audit", href: "/discord/admin/server-audit", permission: "audit.view", group: "Operations", icon: "clipboard" },
  { label: "Audit", href: "/discord/admin/audit", permission: "audit.view", group: "Operations", icon: "audit" },
  { label: "System", href: "/discord/admin/system", permission: "system.health.view", group: "Operations", icon: "pulse" },
];
const navGroups = ["Workspace", "Community", "Store", "Configuration", "Operations"];

// Keeps the tab title in step with the page; the server sends the same titles for direct visits.
function useDocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    const clean = pathname.replace(/\/+$/, "") || "/";
    if (clean === "/store" || clean.startsWith("/store/")) return; // store pages title themselves from the category they show
    const page = seoPages.pages.find((item) => item.path === clean || item.aliases?.includes(clean));
    const board = clean.match(/^\/discord\/leaderboard\/([a-z]+)$/)?.[1] as keyof typeof seoPages.leaderboards | undefined;
    const admin = adminNav.find((item) => clean.startsWith(item.href));
    document.title = page ? page.title
      : board && seoPages.leaderboards[board] ? `${seoPages.leaderboards[board]} Leaderboard – SkyBoxy Discord`
      : admin ? `${admin.label} – SkyBoxy Dashboard`
      : clean.startsWith("/discord/portal") ? "Member portal – SkyBoxy"
      : clean.startsWith("/staff") ? "Staff hub – SkyBoxy"
      : "SkyBoxy";
  }, [pathname]);
}

export function App() {
  const [me, setMe] = useState<Me>({ user: null, permissions: [] });
  const [loaded, setLoaded] = useState(false);
  useDocumentTitle();
  useEffect(() => { api<Me>("/api/v1/me").then(setMe).catch(() => undefined).finally(() => setLoaded(true)); }, []);
  if (!loaded) return <FullState loading title="Opening SkyBoxy" description="Checking your session…"/>;
  return <SessionContext.Provider value={me}><SiteProvider me={me}><StoreProvider><ToastProvider><ConfirmProvider><StoreDialogs/><Suspense fallback={<FullState loading title="Loading" description="Opening this page…"/>}><Routes>
    <Route path="/" element={<MinecraftServerV2/>}/>
    <Route path="/discord/login" element={<Login me={me}/>}/>
    <Route path="/discord/home" element={<HomeRedirect me={me}/>}/>
    <Route path="/discord/access-denied" element={<AccessDenied/>}/>
    <Route path="/discord/link" element={<Protected me={me} permission="portal.access"><MinecraftLink me={me}/></Protected>}/>
    <Route path="/discord/admin/*" element={<Protected me={me} permission="dashboard.view"><AdminFrame me={me}><AdminRoutes/></AdminFrame></Protected>}/>
    <Route path="/members/:key" element={<Protected me={me} permission="members.view"><AdminFrame me={me}><MemberDetailV2/></AdminFrame></Protected>}/>
    <Route path="/discord/portal/*" element={<Protected me={me} permission="portal.access"><Portal me={me}/></Protected>}/>
    <Route path="/staff" element={<Protected me={me} permission="staff.access"><StaffHub/></Protected>}/>
    <Route path="/staff/resources/*" element={<Protected me={me} permission="staff.access"><ResourceRoutes/></Protected>}/>
    <Route path="/staff/tickets/*" element={<Protected me={me} permission="staff.access"><StaffTicketRoutes/></Protected>}/>
    <Route path="/staff/appeals/*" element={<Protected me={me} permission="staff.access"><AppealRoutes/></Protected>}/>
    <Route path="/tickets/:id" element={<TicketRedirect/>}/>
    <Route path="/join" element={<JoinPage/>}/>
    <Route path="/team" element={<TeamPage/>}/>
    <Route path="/appeal" element={<AppealPage/>}/>
    <Route path="/news" element={<NewsIndex/>}/>
    <Route path="/news/:slug" element={<NewsArticle/>}/>
    <Route path="/vote" element={<VotePage/>}/>
    <Route path="/store/cart" element={<CartPage/>}/>
    <Route path="/store/checkout" element={<CheckoutPage/>}/>
    <Route path="/store/wallet" element={<WalletPage/>}/>
    <Route path="/store/orders" element={<OrdersPage/>}/>
    <Route path="/store/orders/:id" element={<OrderPageView/>}/>
    <Route path="/store/*" element={<StorePage/>}/>
    <Route path="/discord/leaderboard" element={<CommunityLeaderboards/>}/>
    <Route path="/discord/leaderboard/:kind" element={<CommunityLeaderboard/>}/>
    <Route path="/minecraft/server" element={<MinecraftServerV2/>}/>
    <Route path="/minecraft/islandtop" element={<MinecraftIslandTopV2/>}/>
    <Route path="*" element={<NotFound/>}/>
  </Routes></Suspense></ConfirmProvider></ToastProvider></StoreProvider></SiteProvider></SessionContext.Provider>;
}

export function Protected({ me, permission, children }: { me: Me; permission: string; children: ReactNode }) {
  const location = useLocation();
  if (!me.user) {
    const query = new URLSearchParams(location.search);
    if (location.pathname === "/discord/link" && /^\d{6}$/.test(query.get("code") || "")) {
      query.set("autoLink", "1");
      return <DiscordLinkLogin returnPath={`${location.pathname}?${query}`}/>;
    }
    return <Navigate to={`/discord/login?returnPath=${encodeURIComponent(location.pathname + location.search)}`} replace/>;
  }
  if (!me.permissions.includes(permission)) return <Navigate to="/discord/access-denied?reason=forbidden" replace/>;
  return children;
}

/* ---------- Auth & account pages (site shell) ---------- */

const loginHref = (returnPath: string) => `/api/v1/auth/login?returnPath=${encodeURIComponent(returnPath)}`;

function DiscordLinkLogin({ returnPath }: { returnPath: string }) {
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    redirectToDiscordLogin(returnPath);
  }, [returnPath]);
  return <MinecraftShell><main className="auth-page" id="main"><section className="auth-card">
    <span className="auth-mark"><span className="spinner" aria-hidden="true"/></span>
    <h1>Sign in to link your account</h1>
    <p>Taking you to Discord now. Once you are signed in, we link the Minecraft code you just made.</p>
    <a className="button discord block" href={loginHref(returnPath)}><DiscordMark/>Continue with Discord</a>
  </section></main></MinecraftShell>;
}

export function MinecraftLink({ me }: { me: Me }) {
  const query = new URLSearchParams(useLocation().search);
  const code = query.get("code") || "";
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [linked, setLinked] = useState(false);
  const valid = /^\d{6}$/.test(code);
  const automatic = query.get("autoLink") === "1";
  const attempted = useRef(false);
  const inFlight = useRef(false);
  const redeem = useCallback(async () => {
    if (!valid || !me.user || inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setMessage(null);
    try {
      const result = await api<{ minecraftName: string; isPrimary: boolean }>("/api/v1/portal/minecraft/link-redeem", {
        method: "POST", body: JSON.stringify({ code, confirm: true }),
      });
      setLinked(true); setMessage({ tone: "success", text: `${result.minecraftName} is now linked as your ${result.isPrimary ? "primary account" : "alt"}.` });
    } catch (error) { setMessage({ tone: "error", text: errorMessage(error, "Linking failed. Please try again.") }); }
    finally { inFlight.current = false; setBusy(false); }
  }, [code, valid, me.user]);
  useEffect(() => {
    if (!automatic || !valid || !me.user || attempted.current) return;
    attempted.current = true;
    void redeem();
  }, [automatic, valid, me.user, redeem]);
  return <MinecraftShell><main className="auth-page" id="main"><section className="auth-card">
    <span className={`auth-mark ${linked ? "tone-success" : ""}`}><Icon name={linked ? "check" : "link"} size={24}/></span>
    <h1>{linked ? "Account linked" : "Link your Minecraft account"}</h1>
    <p>The first account you link becomes your primary. Any you add later count as alts, and the main features keep using your primary.</p>
    <p className="auth-identity"><Icon name="user" size={16}/>Signed in as <strong>{me.user?.displayName}</strong></p>
    {!valid ? <p className="notice tone-error" role="alert"><Icon name="alert" size={18}/><span>This link has no valid code. Run /link in Minecraft to get a new one.</span></p>
      : !linked && <>
        <div className="code-display" aria-label={`Private code ${code}`}>{code.split("").map((digit, index) => <span key={index}>{digit}</span>)}</div>
        <p className="fine-print">Only continue if you made this code yourself with /link on your own Minecraft account.</p>
        <button className="button primary block" disabled={busy} onClick={redeem}>{busy ? "Linking…" : "Confirm account link"}</button>
      </>}
    {message && <p className={`notice tone-${message.tone}`} role="status"><Icon name={message.tone === "success" ? "check" : "alert"} size={18}/><span>{message.text}</span></p>}
    <p className="fine-print">Codes expire after 10 minutes and work only once.</p>
    <Link className="text-link" to="/discord/portal"><Icon name="arrowLeft" size={16}/>Back to your member portal</Link>
  </section></main></MinecraftShell>;
}

function HomeRedirect({ me }: { me: Me }) {
  if (!me.user) return <Navigate to="/discord/login" replace/>;
  const can = (permission: string) => me.permissions.includes(permission);
  return <Navigate to={can("dashboard.view") ? "/discord/admin" : can("portal.access") ? "/discord/portal" : can("staff.access") ? "/staff" : "/"} replace/>;
}

const loginPoints: Array<[IconName, string, string]> = [
  ["lock", "No new password", "You sign in with the Discord account you already use."],
  ["shield", "Your roles open the doors", "The member portal and staff areas follow your roles in the SkyBoxy Discord."],
  ["user", "Only the basics", "We never ask for your email or read your messages."],
];

function Login({ me }: { me: Me }) {
  const location = useLocation();
  const returnPath = new URLSearchParams(location.search).get("returnPath") || "/discord/home";
  if (me.user) return <HomeRedirect me={me}/>;
  return <MinecraftShell><main className="auth-page split" id="main">
    <section className="auth-copy">
      <h1>Sign in to SkyBoxy</h1>
      <p className="lede">Sign in with Discord to open your member portal, link your Minecraft account, or get to the staff hub.</p>
    </section>
    <section className="auth-card" aria-labelledby="login-card-title">
      <img className="auth-crest" src="/assets/skyboxy-crest.webp" alt="" width={96} height={96}/>
      <h2 id="login-card-title">Use your Discord account</h2>
      <p>Discord confirms who you are, then sends you straight back here. SkyBoxy never sees your Discord password.</p>
      <a className="button discord block" href={loginHref(returnPath)}><DiscordMark/>Continue with Discord</a>
      <Link className="text-link" to="/"><Icon name="arrowLeft" size={16}/>Back to the home page</Link>
    </section>
    <section className="auth-why" aria-label="How sign-in works">
      <ul className="auth-points">{loginPoints.map(([icon, title, detail]) => <li key={title}><span className="tile-icon" aria-hidden="true"><Icon name={icon} size={18}/></span><span><strong>{title}</strong><small>{detail}</small></span></li>)}</ul>
    </section>
  </main></MinecraftShell>;
}

function AccessDenied() {
  const reason = new URLSearchParams(useLocation().search).get("reason");
  const retry = reason && reason !== "forbidden" && reason !== "membership_required";
  return <MinecraftShell><main className="auth-page" id="main"><section className="auth-card">
    <span className="auth-mark tone-warning"><Icon name="lock" size={24}/></span>
    <h1>{retry && reason !== "access_denied" ? "Sign-in did not finish" : "You cannot open this page"}</h1>
    <p>{reason === "membership_required" ? "Join the SkyBoxy Discord first, then sign in again to open the member portal."
      : reason === "access_denied" ? "Your Discord roles do not include the member portal, the staff hub, or the dashboard yet."
      : retry ? "Something went wrong on the way back from Discord. Please try again."
      : "Your Discord roles do not include this page. If you think that is a mistake, ask a SkyBoxy admin."}</p>
    <div className="auth-actions"><Link className="button primary" to="/">Back to the home page</Link>{retry ? <Link className="button" to="/discord/login">Try signing in again</Link> : <Link className="button" to="/discord/home">Go to my home</Link>}</div>
  </section></main></MinecraftShell>;
}

function NotFound() {
  return <MinecraftShell><main className="auth-page" id="main"><section className="auth-card">
    <span className="auth-mark"><Icon name="cube" size={24}/></span>
    <h1>Page not found</h1>
    <p>That page does not exist or has moved. Check the address, or head back to somewhere you know.</p>
    <div className="auth-actions"><Link className="button primary" to="/">Back to the home page</Link><Link className="button" to="/minecraft/islandtop">Leaderboards</Link></div>
  </section></main></MinecraftShell>;
}

/* ---------- Admin frame ---------- */

function AdminFrame({ me, children }: { me: Me; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [navQuery, setNavQuery] = useState("");
  const { pathname } = useLocation();
  const menuButton = useRef<HTMLButtonElement>(null);
  const sidebar = useRef<HTMLElement>(null);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    sidebar.current?.querySelector<HTMLElement>(".nav-search input")?.focus();
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") { setOpen(false); menuButton.current?.focus(); } };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);
  const isActive = (item: NavItem) => [item.href, ...(item.aliases || [])].some((prefix) => pathname.startsWith(prefix));
  const { features } = useSite();
  const permitted = adminNav.filter((item) => me.permissions.includes(item.permission) && (!item.feature || features[item.feature]));
  const visible = permitted.filter((item) => item.label.toLowerCase().includes(navQuery.trim().toLowerCase()));
  const current = adminNav.find(isActive);
  const expires = me.expiresAt ? new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date(me.expiresAt)) : null;
  const signOut = () => api("/api/v1/auth/logout", { method: "POST" }).finally(() => location.assign("/"));
  return <div className={`app-layout ${open ? "nav-open" : ""}`}>
    <a className="skip-link" href="#main">Skip to content</a>
    <aside id="admin-navigation" ref={sidebar} className="sidebar" aria-label="Admin navigation">
      <div className="sidebar-brand">
        <Link to="/discord/admin/overview" className="brand-lockup" aria-label="SkyBoxy dashboard home"><img src="/assets/skyboxy-crest.webp" alt="" width={36} height={36}/><span><strong>SkyBoxy</strong><small>Operations</small></span></Link>
        <button className="icon-button ghost sidebar-close" aria-label="Close navigation" onClick={() => { setOpen(false); menuButton.current?.focus(); }}><Icon name="close"/></button>
      </div>
      <label className="nav-search"><Icon name="search" size={16}/><span className="sr-only">Filter admin pages</span><input type="search" value={navQuery} onChange={(event) => setNavQuery(event.target.value)} placeholder="Jump to page"/></label>
      <nav aria-label="Administration" className="sidebar-nav">
        {navGroups.map((group) => { const items = visible.filter((item) => item.group === group); return items.length ? <section className="nav-group" key={group}><h2>{group}</h2>{items.map((item) => <NavLink key={item.href} to={item.href} className={isActive(item) ? "active" : undefined} aria-current={isActive(item) ? "page" : undefined}><Icon name={item.icon}/><span>{item.label}</span></NavLink>)}</section> : null; })}
        {!visible.length && <p className="nav-empty">No pages match “{navQuery}”.</p>}
      </nav>
      <div className="sidebar-foot"><span className="status-dot tone-success" aria-hidden="true"/><span>Protected workspace{expires && <><br/><small>Session until {expires}</small></>}</span></div>
    </aside>
    {open && <button className="sidebar-scrim" aria-label="Close navigation" tabIndex={-1} onClick={() => setOpen(false)}/>}
    <div className="app-main">
      <header className="topbar">
        <button ref={menuButton} className="icon-button menu-button" aria-label="Toggle navigation" aria-controls="admin-navigation" aria-expanded={open} onClick={() => setOpen(!open)}><Icon name="menu"/></button>
        <div className="topbar-title"><span>{current?.group || "Administration"}</span><strong>{current?.label || "Administration"}</strong></div>
        <div className="topbar-actions">
          {me.permissions.includes("staff.access") && <Link className="button ghost compact hide-sm" to="/staff"><Icon name="shield" size={16}/>Staff hub</Link>}
          <Link className="button ghost compact hide-sm" to="/"><Icon name="external" size={16}/>View site</Link>
          <span className="topbar-user" title={me.user?.displayName}><Avatar src={me.user?.avatarUrl} name={me.user?.displayName || "?"} size={30}/><span className="hide-sm">{me.user?.displayName}</span></span>
          <button className="button ghost compact" aria-label="Sign out" onClick={signOut}><Icon name="logout" size={16}/><span className="hide-xs">Sign out</span></button>
        </div>
      </header>
      <main id="main" className="page" tabIndex={-1}>{children}</main>
    </div>
  </div>;
}

function AdminRoutes() {
  return <Suspense fallback={<Skeleton rows={4} label="Loading page"/>}><Routes>
    <Route index element={<Navigate to="overview" replace/>}/>
    <Route path="overview" element={<Overview/>}/>
    <Route path="settings" element={<SettingsV2/>}/>
    <Route path="addons" element={<AddonsV2/>}/>
    <Route path="commands" element={<CommandsV2/>}/>
    <Route path="reaction-roles" element={<RoleStudioV2/>}/>
    <Route path="members" element={<MembersV2/>}/>
    <Route path="members/:key" element={<MemberDetailV2/>}/>
    <Route path="punishments" element={<Punishments/>}/>
    <Route path="logs" element={<Logs/>}/>
    <Route path="memberportal" element={<PortalAdminV2/>}/>
    <Route path="staff" element={<StaffDirectory/>}/>
    <Route path="staff/:id" element={<StaffDetail/>}/>
    <Route path="tickets" element={<TicketInsights/>}/>
    <Route path="news" element={<NewsAdmin/>}/>
    <Route path="store" element={<StoreManager/>}/>
    <Route path="orders" element={<OrdersAdminPage/>}/>
    <Route path="store-settings" element={<StoreSettingsPage/>}/>
    <Route path="gift-cards" element={<GiftCardsPage/>}/>
    <Route path="orbs" element={<OrbsPage/>}/>
    <Route path="website" element={<SiteSettings/>}/>
    <Route path="page-access" element={<PageAccess/>}/>
    <Route path="server-audit" element={<ServerAuditPage/>}/>
    <Route path="minecraft" element={<MinecraftAdmin/>}/>
    <Route path="audit" element={<Audit/>}/>
    <Route path="system" element={<System/>}/>
    <Route path="*" element={<EmptyState icon="search" title="Admin page unavailable" description="This page does not exist. Choose a destination from the navigation." action={<Link className="button" to="/discord/admin/overview">Go to overview</Link>}/>}/>
  </Routes></Suspense>;
}
